import React from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import { ListGroup, ListGroupItem } from 'reactstrap';

import ContextMenu from 'app/ContextMenu/components/ContextMenu';
import { noop } from 'lib/noop';
import { makeId } from 'utils/selectors';

import { SEARCH_RESULTS } from '../const';
import styles from './Results.css';

/**
 * Renders search results
 * @param {object} props - props passed to this component
 * @returns {React.Component} search results
 */
const Results = ({
  context,
  currPageNum,
  getResultActions,
  isResultsLoading,
  onActionSelect,
  onFinish,
  onResultClick,
  pageNumStart,
  resultActions,
  results,
  storeResultPageNum
}) => {
  const togglePage = event => {
    const currPage = Number(event.target.id);
    storeResultPageNum(currPage, pageNumStart);
  };

  const viewMoreResults = () => {
    storeResultPageNum(
      pageNumStart + SEARCH_RESULTS.pageNumsPerPage,
      pageNumStart + SEARCH_RESULTS.pageNumsPerPage
    );
  };

  const viewLessResults = () => {
    storeResultPageNum(
      pageNumStart - SEARCH_RESULTS.pageNumsPerPage,
      pageNumStart - SEARCH_RESULTS.pageNumsPerPage
    );
  };

  const resultsBeginningSlice = (currPageNum - 1) * SEARCH_RESULTS.resultsPerPage;
  const resultsEndingSlice = resultsBeginningSlice + SEARCH_RESULTS.resultsPerPage;
  const currentResults = results.slice(resultsBeginningSlice, resultsEndingSlice);

  const pageNumbers = [
    ...Array(Math.ceil(results.length / SEARCH_RESULTS.resultsPerPage) + 1).keys()
  ].slice(1);

  const currPageNums = pageNumbers.slice(
    pageNumStart - 1,
    pageNumStart - 1 + SEARCH_RESULTS.pageNumsPerPage
  );

  const maxPageNum = Math.floor(results.length / SEARCH_RESULTS.resultsPerPage);

  return (
    <div>
      {isResultsLoading && <p className={styles.resultsHeader}>Loading...</p>}
      {!isResultsLoading && !currentResults.length && (
        <p className={styles.resultsHeader}>No results found</p>
      )}
      <ListGroup className={classnames({ [styles.listGroup]: results.length })}>
        {currentResults.map(result => {
          const {
            ComponentName = SEARCH_RESULTS.unnamedComponent,
            PageName = SEARCH_RESULTS.unnamedPage
          } = result;

          const displayName =
            PageName !== SEARCH_RESULTS.unnamedPage ? PageName : ComponentName;

          const actions = resultActions || getResultActions(result, context);

          return (
            <ListGroupItem key={result.Id}>
              <div className="d-flex justify-content-between align-items-center w-100">
                <span
                  role="presentation"
                  className={classnames(styles.listItemText, 'd-block text-truncate w-100')}
                  title={displayName}
                  data-test-id={makeId({
                    component: 'results',
                    value: displayName
                  })}
                  onClick={() => {
                    onResultClick(result, context);
                    storeResultPageNum(currPageNum, pageNumStart);
                    onFinish();
                  }}
                >
                  {displayName}
                </span>
                {Array.isArray(actions) && (
                  <ContextMenu
                    actions={actions}
                    onActionSelect={action => onActionSelect(result, action, context)}
                    popoverPlacement="left"
                    rem={1.5}
                  />
                )}
              </div>
            </ListGroupItem>
          );
        })}
        {!isResultsLoading && (
          <div>
            <ListGroupItem className={styles.pagination} color="secondary">
              {pageNumStart > 1 && (
                <button type="button" onClick={viewLessResults}>
                  {`<<`}
                </button>
              )}
              {currPageNums.map(number =>
                number === currPageNum ? (
                  <button
                    type="button"
                    key={number}
                    id={number}
                    className={styles.currPage}
                    onClick={togglePage}
                  >
                    {number}
                  </button>
                ) : (
                  <button type="button" key={number} id={number} onClick={togglePage}>
                    {number}
                  </button>
                )
              )}
              {currPageNums.length <= 1 || currPageNums.includes(maxPageNum) ? null : (
                <button type="button" onClick={viewMoreResults}>
                  {`>>`}
                </button>
              )}
            </ListGroupItem>
          </div>
        )}
      </ListGroup>
    </div>
  );
};

Results.propTypes = {
  context: propTypes.string.isRequired,
  currPageNum: propTypes.number,
  getResultActions: propTypes.func,
  isResultsLoading: propTypes.bool.isRequired,
  onActionSelect: propTypes.func,
  onFinish: propTypes.func,
  onResultClick: propTypes.func,
  pageNumStart: propTypes.number,
  resultActions: propTypes.arrayOf(
    propTypes.shape({
      text: propTypes.string.isRequired,
      action: propTypes.string.isRequired
    })
  ),
  results: propTypes.arrayOf(
    propTypes.shape({
      ComponentName: propTypes.string,
      DocType: propTypes.string,
      Id: propTypes.string,
      Name: propTypes.string,
      PageName: propTypes.string,
      Version: propTypes.string
    })
  ).isRequired,
  storeResultPageNum: propTypes.func
};

Results.defaultProps = {
  currPageNum: 1,
  getResultActions: noop,
  onActionSelect: null,
  onFinish: undefined,
  onResultClick: undefined,
  pageNumStart: 1,
  resultActions: null,
  storeResultPageNum: noop
};

export default Results;
